import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { getConferenceName } from '../../../base/conference/functions';
import { getInviteURL } from '../../../base/connection/functions';
import Icon from '../../../base/icons/components/Icon';
import { IconCopy, IconSecurityOn, IconShare } from '../../../base/icons/svg';
import { getLocalParticipant, getParticipantDisplayName, getRemoteParticipants, isParticipantModerator } from '../../../base/participants/functions';
import { copyText } from '../../../base/util/copyText.web';
import { formatMeetingIdForCopy } from '../../../invite/functions';
import { showSuccessNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../notifications/constants';
import Drawer from '../../../toolbox/components/web/Drawer';
import JitsiPortal from '../../../toolbox/components/web/JitsiPortal';
import logger from '../../logger';
/** 抽屉内操作图标尺寸。 */
const DETAILS_ACTION_ICON_SIZE_PX = 16;
const useStyles = makeStyles()(theme => {
    return {
        sheet: {
            padding: '0 16px 24px',
            color: theme.palette.text01,
            boxSizing: 'border-box'
        },
        handle: {
            width: '36px',
            height: '4px',
            borderRadius: '2px',
            backgroundColor: theme.palette.ui04,
            margin: '10px auto 16px'
        },
        title: {
            ...theme.typography.heading5,
            fontWeight: 700,
            margin: '0 0 10px',
            wordBreak: 'break-word'
        },
        security: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: theme.palette.text03,
            ...theme.typography.labelRegular,
            marginBottom: '16px'
        },
        rows: {
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
        },
        row: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            ...theme.typography.bodyShortRegular
        },
        label: {
            flex: '0 0 72px',
            color: theme.palette.text03,
            lineHeight: 1.5
        },
        value: {
            flex: 1,
            minWidth: 0,
            color: theme.palette.text01,
            lineHeight: 1.5,
            wordBreak: 'break-all'
        },
        valueRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px'
        },
        action: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            border: 'none',
            background: 'transparent',
            color: theme.palette.action01,
            cursor: 'pointer',
            padding: 0,
            ...theme.typography.labelRegular,
            '&:hover': {
                opacity: 0.85
            }
        },
        hint: {
            color: theme.palette.text03,
            ...theme.typography.labelRegular
        }
    };
});
/**
 * 会议号展示为 xxx-xxx-xxxx。
 *
 * @param {string} meetingId - 原始会议号。
 * @returns {string} 展示用会议号。
 */
function formatMeetingIdDisplay(meetingId) {
    return formatMeetingIdForCopy(meetingId) || meetingId;
}
/**
 * 手机会议详情底部抽屉：会议主题、会议号、发起人、我的名称、参会时长。
 *
 * @param {IProps} props - 组件属性。
 * @returns {JSX.Element}
 */
const MobileMeetingDetailsDrawer = ({ durationText, isOpen, onClose }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const conferenceName = useSelector(getConferenceName);
    const room = useSelector((state) => state['features/base/conference'].room || '');
    const inviteUrl = useSelector((state) => {
        try {
            return getInviteURL(state);
        }
        catch {
            return '';
        }
    });
    const localParticipant = useSelector(getLocalParticipant);
    const organizerName = useSelector((state) => {
        const local = getLocalParticipant(state);
        if (local && isParticipantModerator(local)) {
            return getParticipantDisplayName(state, local.id);
        }
        for (const participant of getRemoteParticipants(state).values()) {
            if (isParticipantModerator(participant)) {
                return getParticipantDisplayName(state, participant.id);
            }
        }
        return local ? getParticipantDisplayName(state, local.id) : '';
    });
    const meetingTitle = conferenceName || room || t('conferenceHeader.appName');
    const meetingId = room || '';
    const meetingIdDisplay = meetingId ? formatMeetingIdDisplay(meetingId) : '';
    const myName = localParticipant?.name || '';
    const myId = localParticipant?.id || '';
    const onCopyMeetingId = useCallback(async () => {
        if (!meetingId) {
            return;
        }
        const ok = await copyText(meetingId);
        sendAnalytics(createToolbarEvent('mobile.details.copyMeetingId', { ok }));
        if (ok) {
            logger.info(`[手机会议详情] ${new Date().toISOString()} 复制会议号成功 | meetingId=${meetingId}`);
            dispatch(showSuccessNotification({
                titleKey: 'addPeople.meetingIdCopied'
            }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
        }
        else {
            logger.error(`[手机会议详情] ${new Date().toISOString()} 复制会议号失败`);
        }
    }, [dispatch, meetingId]);
    const onCopyInviteLink = useCallback(async () => {
        if (!inviteUrl) {
            return;
        }
        const ok = await copyText(String(inviteUrl));
        sendAnalytics(createToolbarEvent('mobile.details.copyInviteLink', { ok }));
        if (ok) {
            logger.info(`[手机会议详情] ${new Date().toISOString()} 复制邀请链接成功`);
            dispatch(showSuccessNotification({
                titleKey: 'addPeople.linkCopied'
            }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
        }
        else {
            logger.error(`[手机会议详情] ${new Date().toISOString()} 复制邀请链接失败`);
        }
    }, [dispatch, inviteUrl]);
    return (React.createElement(JitsiPortal, null,
        React.createElement(Drawer, { isOpen: isOpen, onClose: onClose },
            React.createElement("div", { className: classes.sheet, role: 'dialog' },
                React.createElement("div", { className: classes.handle }),
                React.createElement("h2", { className: classes.title }, meetingTitle),
                React.createElement("div", { className: classes.security },
                    React.createElement(Icon, { size: DETAILS_ACTION_ICON_SIZE_PX, src: IconSecurityOn }),
                    React.createElement("span", null, t('conferenceHeader.meetingEncrypted'))),
                React.createElement("div", { className: classes.rows },
                    React.createElement("div", { className: classes.row },
                        React.createElement("span", { className: classes.label }, t('conferenceHeader.meetingIdLabel')),
                        React.createElement("div", { className: classes.value },
                            React.createElement("div", { className: classes.valueRow },
                                React.createElement("span", null, meetingIdDisplay || '—'),
                                meetingId ? (React.createElement("button", { "aria-label": t('conferenceHeader.copyMeetingId'), className: classes.action, onClick: onCopyMeetingId, type: 'button' },
                                    React.createElement(Icon, { size: DETAILS_ACTION_ICON_SIZE_PX, src: IconCopy }))) : null,
                                inviteUrl ? (React.createElement("button", { "aria-label": t('conferenceHeader.copyInviteLink'), className: classes.action, onClick: onCopyInviteLink, type: 'button' },
                                    React.createElement(Icon, { size: DETAILS_ACTION_ICON_SIZE_PX, src: IconShare }),
                                    React.createElement("span", null, t('conferenceHeader.inviteLink')))) : null))),
                    React.createElement("div", { className: classes.row },
                        React.createElement("span", { className: classes.label }, t('conferenceHeader.organizerLabel')),
                        React.createElement("span", { className: classes.value }, organizerName || '—')),
                    React.createElement("div", { className: classes.row },
                        React.createElement("span", { className: classes.label }, t('conferenceHeader.myNameLabel')),
                        React.createElement("span", { className: classes.value },
                            myName || '—',
                            myId ? (React.createElement("span", { className: classes.hint }, ` (${t('conferenceHeader.participantIdLabel')}: ${myId})`)) : null)),
                    React.createElement("div", { className: classes.row },
                        React.createElement("span", { className: classes.label }, t('conferenceHeader.meetingDurationLabel')),
                        React.createElement("span", { className: classes.value }, durationText)))))));
};
export default MobileMeetingDetailsDrawer;
